import { X, Download, FileText, Headphones, Play, Wand2 } from 'lucide-react';
import { useState, useRef } from 'react';
import { CachedImage } from './CachedImage';
import { BottomSheet } from './BottomSheet';

interface MediaPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  asset: any | null;
  onDownload: () => void;
}

export const MediaPreviewModal = ({ isOpen, onClose, asset, onDownload }: MediaPreviewModalProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showEditPopover, setShowEditPopover] = useState(false);
  const [activeEditForm, setActiveEditForm] = useState<string | null>(null);
  const [editFormTitle, setEditFormTitle] = useState<string>('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  if (!isOpen || !asset) return null;

  const getMediaType = (asset: any): 'image' | 'video' | 'audio' => {
    if (asset.media_type) {
      return asset.media_type;
    }
    
    const extension = asset.file_extension?.toLowerCase() || asset.public_url.split('.').pop()?.toLowerCase() || '';
    
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff', 'ico'].includes(extension)) {
      return 'image';
    }
    
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
      return 'video';
    }
    
    if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'].includes(extension)) {
      return 'audio';
    }
    
    return 'image';
  };

  const mediaType = getMediaType(asset);

  const togglePlayPause = () => {
    if (mediaType === 'video' && videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    } else if (mediaType === 'audio' && audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleEditFormClose = () => {
    setActiveEditForm(null);
    setEditFormTitle('');
  };

  const renderEditForm = () => {
    // For now, show coming soon message for all forms
    return (
      <div className="p-6 text-center text-th-secondary space-y-4">
        <Wand2 className="w-16 h-16 mx-auto mb-4 text-brand" />
        <h3 className="text-lg font-semibold text-th-primary">{editFormTitle}</h3>
        <p>This editing feature is coming soon...</p>
        <button onClick={handleEditFormClose} className="button-primary mt-4">
          Close
        </button>
      </div>
    );
  };

  const renderMedia = () => {
    switch (mediaType) {
      case 'image':
        return (
          <div className="absolute inset-0 flex items-center justify-center bg-black p-4">
            <CachedImage
              src={asset.public_url}
              alt="Asset preview"
              className="max-w-full max-h-full object-contain"
              style={{ 
                maxWidth: 'calc(100vw - 2rem)',
                maxHeight: 'calc(100vh - 2rem)',
                width: 'auto',
                height: 'auto'
              }}
              fallback={
                <div className="w-full h-64 flex items-center justify-center text-th-secondary bg-surface">
                  <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                  </svg>
                </div>
              }
            />
          </div>
        );
      
      case 'video':
        return (
          <div className="absolute inset-0 bg-black flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
            <video
              ref={videoRef}
              src={asset.public_url}
              className="max-w-full max-h-full object-contain"
              style={{ 
                maxWidth: 'calc(100vw - 2rem)',
                maxHeight: 'calc(100vh - 2rem)',
                width: 'auto',
                height: 'auto'
              }}
              autoPlay
              muted={false}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onClick={togglePlayPause}
              playsInline
              controls
            />
            
            {/* Center Play Button - Always visible when paused */}
            {!isPlaying && (
              <div
                className="absolute inset-0 flex items-center justify-center"
                onClick={togglePlayPause}
              >
                  <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-all cursor-pointer">
                    <Play className="w-10 h-10 text-white ml-1" />
                  </div>
              </div>
            )}

          </div>
        );
      
      case 'audio':
        return (
          <div className="absolute inset-0 bg-brand/20 flex flex-col items-center justify-center p-4">
            <div className="w-32 h-32 bg-brand/20 rounded-full flex items-center justify-center mb-8">
              <Headphones className="w-16 h-16 text-brand" />
            </div>
            <audio
              ref={audioRef}
              src={asset.public_url}
              className="w-full max-w-md bg-elevated rounded-lg"
              autoPlay
              muted={false}
              controls
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />
            <p className="text-th-primary mt-4 font-medium text-center">{asset.file_extension?.toUpperCase()} Audio</p>
          </div>
        );
      
      default:
        return (
          <div className="absolute inset-0 bg-surface flex items-center justify-center">
            <div className="text-center">
              <FileText className="w-16 h-16 text-th-secondary mx-auto mb-4" />
              <p className="text-th-secondary">Cannot preview this file type</p>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black"
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 99999,
          margin: 0,
          padding: 0
        }}
        onClick={() => setShowControls(!showControls)}
      >
        {/* Media Content - Full Screen */}
        {renderMedia()}

        {/* Header Controls */}
        {showControls && (
          <div
            className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent backdrop-blur-sm"
            style={{ zIndex: 100001 }}
          >
              <div className="flex items-center justify-between p-4">
                <button
                  onClick={onClose}
                  className="p-2 text-white hover:text-brand transition-colors rounded-full bg-black/20"
                >
                  <X className="w-6 h-6" />
                </button>
                
                <button
                  onClick={onDownload}
                  className="flex items-center space-x-2 px-3 py-2 bg-overlay hover:bg-muted text-th-primary border border-subtle rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">Download</span>
                </button>
              </div>
          </div>
        )}

        {/* Floating Edit with AI Button */}
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2">
          <button
            onClick={() => setShowEditPopover(!showEditPopover)}
            className="flex items-center space-x-2 px-6 py-3 bg-brand text-surface rounded-xl font-semibold transition-all hover:scale-105 hover:shadow-2xl hover:shadow-brand/40 active:scale-[0.98] shadow-lg shadow-brand/25"
          >
            <Wand2 className="w-5 h-5" />
            <span>Edit with AI</span>
          </button>

          {/* Edit Options Pop-over */}
          {showEditPopover && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-transparent z-10"
                onClick={() => setShowEditPopover(false)}
              />
              
              {/* Pop-over Menu */}
              <div className="absolute bottom-full mb-4 left-1/2 transform -translate-x-1/2 w-80 max-h-96 z-20">
                <div className="bg-elevated border border-subtle rounded-xl shadow-2xl shadow-black overflow-hidden">
                  {/* Header */}
                  <div className="p-4 border-b border-subtle">
                    <h3 className="text-lg font-semibold text-th-primary flex items-center space-x-2">
                      <Wand2 className="w-5 h-5 text-brand" />
                      <span>Edit {mediaType === 'image' ? 'Image' : mediaType === 'video' ? 'Video' : 'Audio'}</span>
                    </h3>
                  </div>

                  {/* Options */}
                  <div className="max-h-80 overflow-y-auto">
                    <div className="p-6 text-center">
                      <Wand2 className="w-12 h-12 text-brand mx-auto mb-4" />
                      <h4 className="text-lg font-semibold text-th-primary mb-2">
                        Editing Options
                      </h4>
                      <p className="text-th-secondary text-sm">
                        All editing features are now available via public pages. 
                        Check the admin panel to create editing workflows.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

      </div>

      {/* Edit Form Bottom Sheet */}
      <BottomSheet
        isOpen={!!activeEditForm}
        onClose={handleEditFormClose}
        title={editFormTitle}
        height="full"
        showHandle={false}
        showHeader={true}
      >
        {renderEditForm()}
      </BottomSheet>
    </>
  );
};